/*
  The workflow phases, ported from orgchart.html:5771–5920.

  The phase bar is the app's primary navigation and the thing that makes the tool a method rather than a
  chart with settings: Data → (Target) → Perimeter → Transfers/Day 1 → TSA/Day 2 → RemainCo, each with a
  live chip saying where that phase currently stands.

  A phase is a LENS, not data. The single-file app says so in a comment on the field itself (`:1926` —
  "a UI lens, not data"), and here that is enforced rather than stated: selecting a phase changes this
  tab's perspective and stage and emits no command, so two people can work the Perimeter and the TSA
  register on the same project at the same time.

  Two things travel with it that ARE project data, and so are not decided here: whether the optional
  Target phase exists at all (`state.targetEnabled`, a command), and the numbers in every chip (functions
  of the design). This module only decides which phases are visible, what lens each one implies, and how
  to say what a phase has achieved so far.
*/
import type { Perspective, Stage } from '@integrate/contracts';
import {
  computeStageBridge,
  computeStrandedTimeline,
  computeDiagnostics,
  fmt,
  fmtMoneyCompact,
  type SeparationState,
} from '@integrate/core';

export type PhaseId = 'data' | 'target' | 'perimeter' | 'transfers' | 'tsa' | 'remainco';

export interface Phase {
  id: PhaseId;
  label: string;
  /** Shown as the button's tooltip, verbatim from the app. */
  title: string;
  /** The Target phase only exists when the project has asked for it. */
  optional?: boolean;
}

export const PHASES: readonly Phase[] = [
  {
    id: 'data',
    label: 'Data (as-is)',
    title: 'Import the employee list, map columns (incl. Dedication), assign heads, fix data',
  },
  {
    id: 'target',
    label: 'Target org design',
    optional: true,
    title:
      'Design the carve-out future-state org on the canvas — add, merge, move and remove units (people are placed later, at Perimeter / Day 1)',
  },
  {
    id: 'perimeter',
    label: 'Perimeter',
    title: 'Define who is in the carve-out as a rule — dedication threshold — or manually',
  },
  {
    id: 'transfers',
    label: 'Transfers & Day 1',
    title: 'Add shared employees (transfers) and build-ups for the Day 1 organisation',
  },
  {
    id: 'tsa',
    label: 'TSA & Day 2',
    title: 'TSA register, replacement build-ups and rightsizing towards the stand-alone organisation',
  },
  {
    id: 'remainco',
    label: 'RemainCo',
    title:
      'Stranded cost of the staying organisation: TSA income coverage, rightsize / redeploy plan, steady state',
  },
];

export function visiblePhases(state: SeparationState): Phase[] {
  return PHASES.filter((p) => !p.optional || state.targetEnabled);
}

export function phaseById(id: PhaseId): Phase {
  return PHASES.find((p) => p.id === id) ?? PHASES[0]!;
}

/**
 * The lens a phase implies, ported from `setPhase` (`:5888`).
 *
 * Freely changeable afterwards — picking a phase sets the natural view for the work it describes, it does
 * not lock it. Data and Target sit on the as-is perspective deliberately: you are looking at the whole
 * organisation, and carve-out membership shows as card ribbons rather than by hiding anyone.
 */
/**
 * The lens fields a phase decides, named exhaustively on purpose.
 *
 * `setPhase` REPLACES the lens object rather than merging into it, so anything the lens carries and
 * this type omits is silently cleared on every phase click. That was lossless while the lens was three
 * booleans a phase sets anyway; the moment filters joined it, narrowing to Germany and pressing "Next"
 * would drop the filter with no message — and the hidden-by-filters hint would stop firing at the same
 * instant, making the disappearance doubly invisible. Naming every field turns the next such omission
 * into a type error.
 */
export interface PhaseLens {
  perspective: Perspective;
  stage: Stage;
  /** Draw the designed organisation rather than the real one. Per-user, like the rest of the lens. */
  targetView: boolean;
}

export function lensForPhase(id: PhaseId): PhaseLens {
  switch (id) {
    case 'data':
      return { perspective: 'as-is', stage: 'day1', targetView: false };
    // The Target phase IS the blueprint view: choosing it switches the chart, leaving it switches back
    // (`isTargetView`, orgchart.html:5783). The perspective underneath is as-is, because a designed
    // unit has no bucket of its own — the people placed in it carry theirs.
    case 'target':
      return { perspective: 'as-is', stage: 'day1', targetView: true };
    case 'perimeter':
      return { perspective: 'as-is', stage: 'perimeter', targetView: false };
    case 'remainco':
      // The Remain perspective IS the staying organisation, which is what this phase costs.
      return { perspective: 'remain', stage: 'day1', targetView: false };
    case 'transfers':
      return { perspective: 'carve-out', stage: 'day1', targetView: false };
    case 'tsa':
      return { perspective: 'carve-out', stage: 'day2', targetView: false };
  }
}

/** A phase that is no longer visible cannot stay selected — the app falls back to Data (`:5892`). */
export function resolvePhase(state: SeparationState, id: PhaseId): PhaseId {
  return visiblePhases(state).some((p) => p.id === id) ? id : 'data';
}

/**
 * The one-line status under each phase's label (`:5845`).
 *
 * Every number is a function of the design, computed with the same engines the server and the deck use.
 * The bridge is lens-free by construction, which is why a chip does not change when you switch tabs to a
 * different perspective — it reports the design, not the view.
 */
export function phaseChip(state: SeparationState, id: PhaseId): string {
  if (id === 'data') {
    const d = computeDiagnostics(state);
    const checks = d.errors.length + d.warnings.length;
    return `${fmt(state.employees.length)} people${
      checks ? ` · ${checks} check${checks === 1 ? '' : 's'}` : ' · clean'
    }`;
  }

  if (id === 'target') {
    const units = state.targetUnits.length;
    return units ? `${fmt(units)} unit${units === 1 ? '' : 's'}` : 'design the boxes';
  }

  const hc = computeStageBridge(state);

  if (id === 'perimeter') {
    const threshold = state.design.perimeterThreshold;
    if (threshold != null) return `≥ ${Math.round(threshold * 100)}% · ${fmt(hc.perimeter)} in`;
    return hc.perimeter ? `${fmt(hc.perimeter)} in · no rule` : 'no rule';
  }

  if (id === 'transfers') {
    return `${fmt(hc.transfers)} transfer${hc.transfers === 1 ? '' : 's'} · +${fmt(
      hc.d1BuildUps,
    )} → ${fmt(hc.day1)}`;
  }

  if (id === 'tsa') {
    const n = state.design.tsas.length;
    return `${fmt(n)} TSA${n === 1 ? '' : 's'} · −${fmt(hc.rightsizing)} · +${fmt(
      hc.tsaRepl + hc.d2BuildUps,
    )} → ${fmt(hc.day2)}`;
  }

  // RemainCo: gross stranded run-rate → residual steady state after the mitigation plan. Without any
  // dedication data there is no way to tell stranded capacity from ordinary cost, so say that instead of
  // reporting a confident zero.
  if (!state.employees.some((e) => e.dedication != null)) return 'needs dedication data';
  const t = computeStrandedTimeline(state);
  return `${fmtMoneyCompact(t.grossRunRate)} stranded → ${fmtMoneyCompact(t.residualRunRate)} residual`;
}
